import { fetchArchivedDealByDealId } from '@/hooks/fetch-store-and-stitch/use-archived-deals.ts';
import { Favorite } from '@/models/registry';
import type { Favorite as FavoriteType, UserFavorite } from '@/models/types';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';
import { dealStore } from './deals';

class FavoriteStore implements BaseStore<Favorite, FavoriteType> {
  setOfDealIds: Set<string> = new Set();

  private fetchingById: Map<string, Promise<void>> = new Map();
  private pendingIds: Set<string> = new Set();
  // 404s
  private deadIds: Set<string> = new Set();

  constructor() {
    makeAutoObservable(this);

    // DEV note: since favorites depend on deal stores, we need to re-fetch favorites when archivedDealStore changes
    // most often happens when arriving at archived-deals page, due to store being cleared immediately for filter purposes
    reaction(
      () => [dealStore.allArchivedDeals.length, dealStore.all.length],
      () => {
        for (const dealId of this.setOfDealIds) {
          const inActive = !!dealStore.getById(dealId);
          if (!inActive) {
            this.enqueueIfMissing(dealId);
          }
        }
        void this.fetchMissingArchivedDeals();
      },
    );
  }

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<FavoriteType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.favorites.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.favorites.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (hydrateIndexedDB) {
      db.favorites.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<FavoriteType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<FavoriteType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (hydrateIndexedDB) {
      db.favorites.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.data.deal_id);
      return;
    }

    this.add(delta.data);
  }

  add(favorite: FavoriteType) {
    if (this.setOfDealIds.has(favorite.deal_id)) {
      return;
    }
    this.setOfDealIds.add(favorite.deal_id);
    this.deadIds.delete(favorite.deal_id);
    this.enqueueIfMissing(favorite.deal_id);
  }

  remove(dealId: string) {
    this.setOfDealIds.delete(dealId);
    this.pendingIds.delete(dealId);
    this.fetchingById.delete(dealId);
    this.deadIds.delete(dealId);
  }

  isFavoritedByUser(dealId: string): boolean {
    return this.setOfDealIds.has(dealId);
  }

  get favorites() {
    const result: Array<UserFavorite & { dealShortId: string }> = [];

    for (const dealId of this.setOfDealIds) {
      const deal = dealStore.getById(dealId);
      if (deal) {
        result.push({
          deal_id: deal.id,
          short_id: deal.short_id,
          full_name: deal.primary_customer?.full_name ?? '',
          status: deal.status,
          dealShortId: deal.short_id,
        });
      } else {
        this.enqueueIfMissing(dealId);
      }
    }

    return result;
  }

  private enqueueIfMissing = (dealId: string) => {
    if (this.deadIds.has(dealId)) return;

    const inDealStore = !!dealStore.getById(dealId);
    const alreadyFetching = this.fetchingById.has(dealId);

    if (!inDealStore && !alreadyFetching) {
      this.pendingIds.add(dealId);
    }
  };

  async fetchMissingArchivedDeals() {
    if (this.pendingIds.size === 0) return;

    const toFetch: string[] = [];
    this.pendingIds.forEach((id) => {
      if (!this.fetchingById.has(id) && !this.deadIds.has(id)) {
        toFetch.push(id);
      }
    });

    if (toFetch.length === 0) return;

    for (const id of toFetch) {
      const task = (async () => {
        try {
          const archivedDeal = await fetchArchivedDealByDealId(id);
          if (archivedDeal) {
            runInAction(() => {
              dealStore.upsertArchivedDeal(archivedDeal);
              this.pendingIds.delete(id);
            });
          } else {
            runInAction(() => {
              this.pendingIds.delete(id);
            });
          }
        } catch (e: any) {
          const status = e?.response?.status ?? e?.status;
          if (status === 404) {
            runInAction(() => {
              this.deadIds.add(id);
              this.pendingIds.delete(id);
            });
          }
        } finally {
          runInAction(() => {
            this.fetchingById.delete(id);
          });
        }
      })();

      this.fetchingById.set(id, task);
    }

    await Promise.allSettled(toFetch.map((id) => this.fetchingById.get(id)!));
  }

  get all(): UserFavorite[] {
    const favorites: UserFavorite[] = [];
    this.setOfDealIds.forEach((dealId) => {
      const deal = dealStore.getById(dealId);
      if (deal) {
        favorites.push({
          deal_id: deal.id,
          short_id: deal.short_id,
          full_name: deal.primary_customer?.full_name ?? '',
          status: deal.status,
        });
      } else {
        this.enqueueIfMissing(dealId);
      }
    });

    return favorites;
  }

  upsert(favorite: FavoriteType) {
    this.add(favorite);
    this.fetchMissingArchivedDeals();
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'favorites',
      entity: 'favorites',
      modelStore: favoriteStore,
    });
  }
}

export const favoriteStore = new FavoriteStore();
